import { ButtonTranslucent, Colors, Icon } from '@amelio/ui-library';

import { PlanLockIcon, usePlanLock } from '@/components/ProGate/PlanLock';
import { MANAGER_ASSESSMENT_SURVEY_IMAGE_URL } from '@/constants/managerAssessment';
import { useAmelioTranslations } from '@/hooks';

type SurveyCompletionScreenProps = {
    dataTestId?: string;
} & (
    | {
          variant: 'ready';
          employeeFirstName: string;
          // True when the evaluation was already submitted and nothing has changed since — shows an
          // "already submitted" message instead of the "click to submit" prompt.
          alreadySubmitted?: boolean;
          isSubmitting?: boolean;
          onSubmit: () => void;
          onPreviousQuestion: () => void;
      }
    | {
          variant: 'submitted';
          onViewResults: () => void;
          onBackToAssessment: () => void;
      }
);

export const SurveyCompletionScreen = (props: SurveyCompletionScreenProps) => {
    const { t } = useAmelioTranslations();
    const planLock = usePlanLock();
    const { dataTestId = 'survey-completion-screen' } = props;

    return (
        <div
            className={
                'relative w-full max-w-[842px] overflow-hidden rounded-[16px] border border-gray-200'
            }
            data-testid={dataTestId}
        >
            <div
                className={'absolute inset-0 bg-linear-to-br from-black via-purple-950/95 to-black'}
            />

            <img
                src={MANAGER_ASSESSMENT_SURVEY_IMAGE_URL}
                alt={'Completion'}
                className={'absolute inset-0 h-full w-full object-cover opacity-25'}
            />

            {props.variant === 'ready' ? (
                <div
                    className={
                        'relative z-10 flex min-h-[300px] flex-col justify-between p-[16px] md:min-h-[400px] md:p-[32px]'
                    }
                >
                    <div className={'flex flex-1 flex-col items-center justify-center'}>
                        <h3
                            className={
                                'max-md:text-subtitle md:text-heading mb-4 max-w-[600px] text-center font-medium text-white md:mb-6'
                            }
                        >
                            {t(
                                props.alreadySubmitted
                                    ? 'managerAssessmentEvaluation.completion.alreadySubmittedTitle'
                                    : 'managerAssessmentEvaluation.completion.readyTitle',
                                { name: props.employeeFirstName },
                            )}
                        </h3>

                        <p
                            className={
                                'text-body-lg mb-[32px] max-w-[550px] text-center text-white/80'
                            }
                        >
                            {t(
                                props.alreadySubmitted
                                    ? 'managerAssessmentEvaluation.completion.alreadySubmittedSubtitle'
                                    : 'managerAssessmentEvaluation.completion.readySubtitle',
                            )}
                        </p>

                        <ButtonTranslucent
                            size={'lg'}
                            loading={!planLock.isLocked && props.isSubmitting}
                            onClick={planLock.isLocked ? planLock.onLocked : props.onSubmit}
                            leftIcon={planLock.isLocked ? <PlanLockIcon /> : undefined}
                            ariaLabel={t('managerAssessmentEvaluation.survey.submit')}
                            dataTestId={'submit-survey-button'}
                        >
                            {t('managerAssessmentEvaluation.survey.submit')}
                        </ButtonTranslucent>
                    </div>

                    <div
                        className={
                            'mt-[32px] flex items-center justify-between gap-[16px] border-t border-white/10 pt-[32px]'
                        }
                    >
                        <button
                            type={'button'}
                            onClick={props.onPreviousQuestion}
                            className={
                                'text-body-sm flex items-center gap-[8px] rounded-[8px] px-[16px] py-[10px] font-medium text-white transition-all hover:bg-white/10'
                            }
                            data-testid={'completion-previous-question'}
                        >
                            <Icon
                                name={'arrow-arrow-left-1'}
                                size={16}
                                color={Colors.WHITE}
                            />

                            <span className={'whitespace-nowrap'}>
                                {t('managerAssessmentEvaluation.survey.previousQuestion')}
                            </span>
                        </button>

                        <div />
                    </div>
                </div>
            ) : (
                <div
                    className={
                        'relative z-10 flex min-h-[500px] flex-col items-center justify-center gap-6 px-8 py-12 text-center'
                    }
                >
                    <div
                        className={
                            'flex h-20 w-20 items-center justify-center rounded-full bg-teal-400/20'
                        }
                    >
                        <Icon
                            name={'essential-tick-circle'}
                            size={40}
                            color={Colors.SECONDARY}
                        />
                    </div>

                    <div className={'flex flex-col gap-3'}>
                        <h2 className={'text-[36px] font-bold text-white'}>
                            {t('managerAssessmentEvaluation.completion.title')}
                        </h2>

                        <p className={'text-lg font-medium text-teal-300'}>
                            {t('managerAssessmentEvaluation.completion.subtitle')}
                        </p>

                        <p className={'mt-2 max-w-[450px] text-sm leading-relaxed text-white/70'}>
                            {t('managerAssessmentEvaluation.completion.description')}
                        </p>
                    </div>

                    <div className={'mt-4 flex flex-col gap-3 sm:flex-row'}>
                        <button
                            type={'button'}
                            onClick={props.onViewResults}
                            className={
                                'flex items-center gap-2 rounded-xl bg-teal-400 px-6 py-3 text-sm font-medium text-white shadow-lg transition-all hover:bg-teal-500 hover:shadow-xl'
                            }
                            data-testid={'completion-view-results'}
                        >
                            <Icon
                                name={'arrow-arrow-right-3'}
                                size={16}
                                color={Colors.WHITE}
                            />

                            {t('managerAssessmentEvaluation.completion.viewResults')}
                        </button>

                        <button
                            type={'button'}
                            onClick={props.onBackToAssessment}
                            className={
                                'flex items-center gap-2 rounded-xl border border-white/20 bg-white/10 px-6 py-3 text-sm font-medium text-white transition-all hover:bg-white/20'
                            }
                            data-testid={'completion-back-to-assessment'}
                        >
                            <Icon
                                name={'arrow-arrow-left-2'}
                                size={16}
                                color={Colors.WHITE}
                            />

                            {t('managerAssessmentEvaluation.completion.backToAssessment')}
                        </button>
                    </div>
                </div>
            )}
        </div>
    );
};
